// High-intent SEO landing pages targeting bottom-of-funnel Florida searches.
// These are NOT damage-type pages — they target the user's specific problem keyword.
// URL slugs are intentionally search-query-shaped for exact-match SEO.

import { images } from "../mock";

export const landingPages = {
  "denied-insurance-claim-florida": {
    slug: "denied-insurance-claim-florida",
    targetKeyword: "denied insurance claim florida",

    // Hero
    eyebrow: "Florida Denied Claim Help · Appeal & Contest Denials",
    h1: "Denied Insurance Claim in Florida? Appeal It & Contest It.",
    h1Highlight: "Appeal It & Contest It.",
    intro:
      "If you have a denied homeowners insurance claim in Florida — a property damage claim denied in Florida, a denied hurricane claim, or a flat-out roof denial — you are not stuck with that decision. Florida law gives you the legal right to appeal an insurance claim denial and contest a denied claim through written dispute, supplemental filing, appraisal, and mediation. We do this every single day for Tampa Bay and statewide Florida homeowners. The denial letter is the carrier's opening position. It is not the final word on your denied property insurance claim.",

    // SEO
    metaTitle:
      "Denied Insurance Claim Florida | Appeal Insurance Claim Denial & Contest Denied Claim | Claim Beast",
    metaDescription:
      "Denied insurance claim in Florida? We appeal insurance claim denials and contest denied claims for homeowners statewide. Denied homeowners insurance claim Florida, property damage claim denied Florida, denied hurricane claim — reopened, supplemented, and recovered. Free Florida claim review. No upfront cost.",
    keywords:
      "denied insurance claim florida, denied homeowners insurance claim florida, property damage claim denied florida, appeal insurance claim denial florida, how to appeal insurance claim denial florida, denied property claim florida, denied homeowners claim florida, appeal denied claim, contest denied claim, appeal denied insurance claim florida, contest denied insurance claim florida, appeal denied property claim florida, contest denied homeowners claim florida, denied home insurance claim florida, reopen denied claim florida, florida claim denial help, insurance company denied my claim florida, hurricane claim denied florida, denied claim public adjuster florida, dispute denied claim florida, overturn denied claim florida",

    image: images.negotiation,
    showStats: true,

    // Pain points / "Why your claim got denied"
    painSectionTitle: "Why a Property Damage Claim Gets Denied in Florida",
    painSectionSub:
      "Carriers in Florida have a playbook. Here are the most common denial reasons we see every week — and why almost none of them are final:",
    painPoints: [
      {
        title: "Wear & tear / pre-existing damage",
        text: "The most overused denial reason in Florida. Carriers blame your roof age, prior repairs, or 'long-term deterioration' to avoid paying a covered storm loss. Documentation defeats this — every time.",
      },
      {
        title: "Late reporting",
        text: "Florida's notice-of-claim deadline is one year for hurricane losses and two years for re-opening. Many denials cite 'late notice' even when you were within the window. We've reopened claims years after the loss.",
      },
      {
        title: "Excluded peril",
        text: "Insurers misclassify wind damage as flood, water as 'constant seepage,' or storm damage as 'mechanical failure' to push your claim into an exclusion. We re-classify it correctly.",
      },
      {
        title: "Missing or incomplete documentation",
        text: "If your original claim was filed without photos, scope, or estimate, carriers exploit the gap. We rebuild the documentation from the ground up.",
      },
      {
        title: "Failure to maintain / negligence",
        text: "A classic catch-all denial. The carrier needs to prove negligence caused the loss. Almost always they can't — when challenged.",
      },
      {
        title: "Policy interpretation disputes",
        text: "Carriers cherry-pick policy language to deny. We bring the policy back to its plain meaning and Florida case law.",
      },
    ],

    // "What to do" steps
    stepsSectionTitle: "What to Do After Your Florida Claim Is Denied",
    steps: [
      {
        n: "01",
        title: "Don't accept the denial letter as final",
        text: "A denial letter is the carrier's opening position, not a final ruling. Florida law gives you the right to dispute, supplement, and reopen.",
      },
      {
        n: "02",
        title: "Save every document",
        text: "Hold onto the denial letter, all carrier emails, your original claim file, photos, estimates, and the insurance policy itself. We will need them.",
      },
      {
        n: "03",
        title: "Do NOT call the carrier to argue",
        text: "Anything you say is documented and can be used against you. Let a licensed public adjuster speak on your behalf.",
      },
      {
        n: "04",
        title: "Call Claim Beast for a free review",
        text: "We'll read the denial letter, your policy, and your evidence — and tell you whether the claim can be reopened. No cost. No obligation.",
      },
      {
        n: "05",
        title: "We rebuild the claim and reopen",
        text: "Proper photo documentation, Xactimate scope, expert reports if needed, and a formal supplemental demand backed by Florida statutes.",
      },
    ],

    // Stats / proof
    statsBlock: {
      headline: "Denied claims we've reopened.",
      stats: [
        { value: "$10.3M+", label: "Recovered for Florida homeowners" },
        { value: "425+", label: "Claims settled" },
        { value: "$0", label: "Upfront cost — no win, no fee" },
      ],
    },

    // FAQ
    faqs: [
      {
        question: "Can a denied insurance claim be reopened in Florida?",
        answer:
          "Yes. Florida law allows policyholders to reopen, supplement, or dispute a denied insurance claim within applicable statutory deadlines (one year for new hurricane claims, two years for supplemental and reopened claims under Fla. Stat. § 627.70132). A denial is the carrier's opening position, not a final ruling.",
      },
      {
        question: "How long do I have to dispute a denied claim in Florida?",
        answer:
          "Under Fla. Stat. § 627.70132, you generally have 1 year to file a new claim after the date of loss for hurricane damage, and 18 months for supplemental/reopened claims. For non-hurricane perils, deadlines vary by policy — we review your specific policy at no cost.",
      },
      {
        question: "Do I need a lawyer to dispute a denied home insurance claim?",
        answer:
          "Not necessarily. A licensed Florida public adjuster can reopen most denied claims, negotiate directly with the carrier, and recover the payout without litigation. We only refer to attorneys when bad-faith litigation is the right move — typically after public adjusting has been exhausted.",
      },
      {
        question: "How much does it cost to hire a public adjuster in Florida?",
        answer:
          "Florida public adjusters work on contingency — meaning no upfront cost and no fee unless we recover money for you. Florida statute caps public adjuster fees, and we follow that cap strictly.",
      },
      {
        question:
          "What's the difference between a denied claim and an underpaid claim?",
        answer:
          "A denied claim means the carrier paid nothing. An underpaid claim means they paid something but the offer doesn't cover the actual cost of repair or replacement. Both can be reopened and supplemented — and we handle both the same way: rebuild the documentation, re-scope the loss, and force the carrier back to the table.",
      },
      {
        question: "What if my insurance company says my damage is wear and tear?",
        answer:
          "'Wear and tear' is the most overused denial reason in Florida. It can almost always be defeated with proper expert documentation — meteorological reports for storm events, certified inspector reports, and photographic timeline evidence. We do this work as part of every reopening.",
      },
      {
        question: "How do I appeal an insurance claim denial in Florida?",
        answer:
          "To appeal an insurance claim denial in Florida, you don't file a formal 'appeal' with a court — you dispute the denial directly with the carrier by rebuilding the claim file. That means obtaining the denial letter and policy, re-documenting the loss with photos and a full Xactimate scope, adding expert or meteorological reports where needed, and submitting a formal supplemental demand citing the covered peril and Florida statute. If the carrier still refuses, appraisal, mediation (Fla. Stat. § 627.7015), or bad-faith litigation are the next escalations. A licensed public adjuster handles this entire appeal process for you.",
      },
      {
        question: "My property damage claim was denied in Florida — can it be reversed?",
        answer:
          "Yes, most property damage claims denied in Florida can be reversed. The majority of denials rely on beatable reasons — wear and tear, 'no functional damage,' misclassified perils, or missing documentation. Once we rebuild the claim with proper evidence and a formal supplemental demand, a large share of denied property damage claims are paid without ever going to court.",
      },
      {
        question: "Can a denied homeowners insurance claim in Florida be reopened?",
        answer:
          "Yes. A denied homeowners insurance claim in Florida can be reopened and supplemented within the statutory windows — generally 1 year to file a hurricane claim and 18 months to reopen or supplement one under Fla. Stat. § 627.70132, with a longer breach-of-contract window in many cases. Send us the denial letter and we'll tell you within 48 hours whether reopening makes sense, at no cost.",
      },
    ],

    // Internal cross-links
    relatedTitle: "Related Florida Claim Help",
    related: [
      {
        text: "Underpaid insurance claim in Florida?",
        href: "/underpaid-insurance-claim-florida",
      },
      {
        text: "Roof claim denied in Florida?",
        href: "/roof-claim-denial-florida",
      },
      { text: "Hurricane / wind damage claim help", href: "/wind-damage" },
      { text: "Water damage claim help", href: "/water-damage" },
      { text: "Hail roof claim help", href: "/hail-damage" },
      { text: "Denied roof insurance claim", href: "/denied-roof-insurance-claim-florida" },
      { text: "Denied water damage claim", href: "/denied-water-damage-claim-florida" },
      { text: "Denied fire damage claim", href: "/denied-fire-damage-claim-florida" },
      { text: "Denied hurricane damage claim", href: "/denied-hurricane-damage-claim-florida" },
      { text: "Denied wind damage claim", href: "/denied-wind-damage-claim-florida" },
      { text: "Denied mold claim", href: "/denied-mold-claim-florida" },
      { text: "Denied pipe leak claim", href: "/denied-pipe-leak-claim-florida" },
      { text: "Denied sewage backup claim", href: "/denied-sewage-backup-claim-florida" },
      { text: "Denied lightning claim", href: "/denied-lightning-claim-florida" },
      { text: "Denied theft / vandalism claim", href: "/denied-theft-vandalism-claim-florida" },
    ],
    extraSections: [
      {
        h2: "Denied Hurricane Isaias Claims in Northwest Florida",
        body: [
          [
            "Hurricane Isaias made landfall near Destin on October 9, 2026 as a Category 2 hurricane, and Panhandle denials now follow a familiar pattern: wind damage attributed to age or wear, water intrusion reclassified as flood or surge, and condominium losses bounced between the master policy and the unit owner's HO-6. If your claim was denied in Escambia, Santa Rosa, Okaloosa, or Walton County, see our ",
            { to: "/pensacola-denied-insurance-claim", label: "Pensacola denied claim" },
            " and ",
            { to: "/destin-denied-insurance-claim", label: "Destin denied claim" },
            " pages, or start at the ",
            { to: "/hurricane-isaias-pensacola", label: "Hurricane Isaias Pensacola" },
            " and ",
            { to: "/hurricane-isaias-destin", label: "Hurricane Isaias Destin" },
            " hubs.",
          ],
        ],
      },
    ],

    guides: [
      {
        tag: "Denied Claims Guide",
        title: "How To Appeal an Insurance Claim Denial in Florida",
        href: "/blog/how-to-appeal-an-insurance-claim-denial-in-florida",
      },
      {
        tag: "Denied Claims Guide",
        title: "Can a Denied Insurance Claim Be Reopened in Florida?",
        href: "/blog/can-a-denied-insurance-claim-be-reopened-in-florida",
      },
      {
        tag: "Process Guide",
        title: "How To Appeal a Denied Property Insurance Claim in Florida",
        href: "/blog/how-to-appeal-a-denied-property-insurance-claim-in-florida",
      },
    ],
  },

  "underpaid-insurance-claim-florida": {
    slug: "underpaid-insurance-claim-florida",
    targetKeyword: "underpaid insurance claim florida",

    eyebrow: "Florida Underpaid Claim Help",
    h1: "Underpaid Insurance Claim in Florida? We Get the Rest.",
    h1Highlight: "Get the Rest.",
    intro:
      "Your insurance carrier paid you something — but it's nowhere near what the repair actually costs. Maybe they sent a $4,200 check for a $40,000 roof. Maybe they paid ACV when your policy covers RCV. Maybe they missed an entire room of damage. That's exactly where insurance underpayment recovery comes in: Florida public adjusters supplement an underpaid claim in Florida every day, and the additional recovery is usually significant. If you have an underpaid claim in Florida, the money the carrier left off the estimate is still recoverable.",

    metaTitle:
      "Underpaid Insurance Claim Florida | Insurance Underpayment Recovery | Claim Beast",
    metaDescription:
      "Underpaid insurance claim in Florida? Claim Beast handles insurance underpayment recovery — we supplement your underpaid claim in Florida and recover the rest carriers owe you. Free review, no upfront cost.",
    keywords:
      "underpaid insurance claim florida, insurance underpayment recovery, insurance underpayment recovery florida, underpaid claim florida, recover underpaid insurance claim florida, lowball insurance offer florida, supplemental claim florida, underpaid homeowners claim florida, insurance underpaid me florida, supplement insurance claim florida, florida public adjuster underpaid claim, ACV vs RCV florida, depreciation dispute florida, insurance settlement too low florida",

    image: images.negotiation,
    showStats: true,

    extraSections: [
      {
        h2: "Underpaid Roof Claims",
        body: [
          [
            "Roofs are the single most underpaid line on Florida claims. Carriers approve a partial-slope repair when the roof needs replacing, depreciate it by age, and omit the code upgrades your repair triggers. See our ",
            { to: "/underpaid-roof-insurance-claim-florida", label: "underpaid roof claim" },
            " page for how we recover a full, code-compliant roof payout, or ",
            { to: "/roof-claim-calculator", label: "estimate your roof claim" },
            " first.",
          ],
        ],
      },
      {
        h2: "Underpaid Hurricane Claims",
        body: [
          [
            "Storm claims combine roof, water, wind, and contents damage — and carriers processing thousands at once routinely underscope every category and misapply the hurricane deductible. Our ",
            { to: "/underpaid-hurricane-claim-florida", label: "underpaid hurricane claim" },
            " page explains how we reassemble the entire loss into one properly documented supplemental demand.",
          ],
          [
            "On the Panhandle, Hurricane Isaias (Category 2 landfall near Destin, October 9, 2026) produced gusts above 100 mph from Pensacola to Navarre and more than 9 inches of rain in parts of Okaloosa County — losses that are routinely underscoped on the first estimate. For storm-specific estimate review, see our ",
            { to: "/pensacola-underpaid-insurance-claim", label: "Pensacola underpaid claim" },
            " and ",
            { to: "/destin-underpaid-insurance-claim", label: "Destin underpaid claim" },
            " pages.",
          ],
        ],
      },
      {
        h2: "Free Estimate Review for Underpaid Claims",
        body: [
          [
            "The fastest way to know if you were shorted is an estimate review: we compare the carrier's scope and pricing against a full replacement-cost estimate, line by line, and flag what's missing. Start with our ",
            { to: "/claim-calculator", label: "free claim calculator" },
            " for a defensible recovery range, then we'll review your carrier's estimate at no cost.",
          ],
        ],
      },
    ],

    painSectionTitle: "How Florida Carriers Underpay Claims",
    painSectionSub:
      "Insurance companies use a precise set of tactics to keep payouts low. Knowing them is half the fight:",
    painPoints: [
      {
        title: "Aggressive depreciation (ACV instead of RCV)",
        text: "Carriers depreciate your roof, contents, and structure heavily — paying actual cash value when your policy entitles you to full replacement cost. We force the RCV recovery your policy provides.",
      },
      {
        title: "Missed scope / 'didn't see it'",
        text: "Carrier adjusters often inspect quickly and miss damage — entire rooms, hidden water intrusion, code upgrade requirements, matching shingles or tile. We re-document from scratch.",
      },
      {
        title: "Improper line-item pricing",
        text: "Xactimate line items get priced below market, labor minimums get skipped, and overhead and profit (O&P) gets removed. We restore every legitimate line item.",
      },
      {
        title: "Mishandled code upgrades",
        text: "Florida Building Code upgrades during repairs are often a covered cost. Carriers leave them out. We add them back.",
      },
      {
        title: "Contents settled at 'garage sale value'",
        text: "Your contents are valued at pennies on the dollar. We prepare proper contents inventories and force fair valuation.",
      },
      {
        title: "Closed claims with damage left unrepaired",
        text: "Carriers close claims aggressively. Florida law allows supplemental and reopened claims when additional damage is discovered or remaining damage was missed.",
      },
    ],

    stepsSectionTitle: "The Insurance Underpayment Recovery Process in Florida",
    steps: [
      {
        n: "01",
        title: "We pull and read your policy",
        text: "Most homeowners have never read their actual policy. We do — line by line — to find every coverage you're entitled to.",
      },
      {
        n: "02",
        title: "Re-inspect and re-document the loss",
        text: "We walk the property, photograph every detail, and identify the damage the carrier missed or underestimated.",
      },
      {
        n: "03",
        title: "Build a proper Xactimate estimate",
        text: "Full replacement-cost scope, fair Florida market pricing, code upgrades, O&P, and contents inventory.",
      },
      {
        n: "04",
        title: "File a formal supplemental demand",
        text: "We submit the supplemental claim with full documentation and the legal basis for additional payment under your policy and Florida statutes.",
      },
      {
        n: "05",
        title: "Negotiate hard — and collect",
        text: "Most supplements settle without litigation. The additional check goes to you. We collect a contingency fee only after you do.",
      },
    ],

    statsBlock: {
      headline: "What we've recovered on underpaid claims.",
      stats: [
        { value: "$10.3M+", label: "Recovered for Florida homeowners" },
        { value: "5–10×", label: "Common multiple of original offer" },
        { value: "$0", label: "Out-of-pocket cost to you" },
      ],
    },

    faqs: [
      {
        question: "What is an underpaid insurance claim in Florida?",
        answer:
          "An underpaid claim is a claim where the insurance company has paid you something — but the payment is less than the actual cost to repair or replace the damage. Common causes include excessive depreciation, missed scope items, improper line-item pricing, denied code upgrades, and lowball contents valuation.",
      },
      {
        question: "How does insurance underpayment recovery work in Florida?",
        answer:
          "Insurance underpayment recovery is the process of getting the carrier to pay the full amount your policy actually owes after a lowball settlement. In Florida it works like this: a licensed public adjuster pulls and reads your policy, re-inspects and re-documents the loss, rebuilds a full replacement-cost Xactimate estimate (with code upgrades, O&P, and proper contents valuation), then files a formal supplemental demand under Fla. Stat. § 627.70132. Most underpayment recoveries settle in 60–120 days without litigation, and you pay nothing upfront — the fee comes only out of the additional money recovered.",
      },
      {
        question: "Can I supplement an underpaid claim in Florida?",
        answer:
          "Yes. Florida law allows policyholders to file supplemental claims to recover additional damages that were not paid in the original settlement. Under Fla. Stat. § 627.70132, supplemental claims for hurricane damage must generally be filed within 18 months of the date of loss.",
      },
      {
        question: "How much more can a public adjuster recover on an underpaid claim?",
        answer:
          "It varies — but in our experience reopening and supplementing Florida claims, additional recoveries of 5 to 10 times the original payment are common. Some claims we've taken from $4,000 lowball offers to $38,000+ final settlements.",
      },
      {
        question: "What is the difference between ACV and RCV in Florida policies?",
        answer:
          "ACV (Actual Cash Value) is replacement cost minus depreciation — what your damaged item is worth today, factoring in age and wear. RCV (Replacement Cost Value) is the full cost to replace it new. Most Florida homeowners policies entitle you to RCV once repairs are made. Carriers often pay only ACV — we force the RCV recovery you're owed.",
      },
      {
        question: "Will reopening my claim cancel my insurance policy in Florida?",
        answer:
          "No. Florida law protects policyholders from non-renewal based solely on filing a legitimate supplemental claim within statutory deadlines. We handle claims the right way to protect your standing with the carrier.",
      },
      {
        question: "How long does a supplemental claim take in Florida?",
        answer:
          "Most supplemental claims settle within 60 to 120 days of filing. Florida statute requires carriers to acknowledge and respond on a strict timeline, and we hold them to it.",
      },
    ],

    relatedTitle: "Related Florida Claim Help",
    related: [
      {
        text: "Insurance claim denied in Florida?",
        href: "/denied-insurance-claim-florida",
      },
      {
        text: "Roof claim denied in Florida?",
        href: "/roof-claim-denial-florida",
      },
      { text: "Hurricane / wind damage claim help", href: "/wind-damage" },
      { text: "Water damage claim help", href: "/water-damage" },
      { text: "Fire & smoke damage claim help", href: "/fire-damage" },
      {
        text: "Read: Insurance underpayment recovery in Florida — how it works",
        href: "/blog/insurance-underpayment-recovery-florida-how-it-works",
      },
    ],
    guides: [
      {
        tag: "Underpaid Claims Guide",
        title: "Insurance Underpayment Recovery in Florida: How It Works",
        href: "/blog/insurance-underpayment-recovery-florida-how-it-works",
      },
      {
        tag: "Underpaid Claims Guide",
        title: "What To Do If Your Insurance Company Underpaid Your Claim",
        href: "/blog/what-to-do-if-your-insurance-company-underpaid-your-claim",
      },
      {
        tag: "Don't Sign Yet",
        title: "Don't Sign an Insurance Estimate Without Reading This",
        href: "/blog/dont-sign-insurance-estimate-without-reading-this",
      },
    ],
  },

  "roof-claim-denial-florida": {
    slug: "roof-claim-denial-florida",
    targetKeyword: "roof claim denial florida",

    eyebrow: "Florida Roof Claim Denial",
    h1: "Roof Claim Denied in Florida? We Get It Approved.",
    h1Highlight: "Get It Approved.",
    intro:
      "Florida has the most denied roof claims in the country. Carriers reflexively deny roof replacements citing roof age, wear and tear, 'cosmetic damage,' or 'no functional damage found.' Most of those denials don't hold up when challenged with proper inspection evidence, meteorological data, and a HAAG-certified scope. We've reopened hundreds of denied Florida roof claims and won.",

    metaTitle:
      "Roof Claim Denial in Florida | Reopen Denied Roof Claim | Claim Beast",
    metaDescription:
      "Denied roof insurance claim in Florida? Claim Beast public adjusters reopen denied roof claims statewide. Hurricane wind, hail, shingle and tile roof denials reversed. Free review.",
    keywords:
      "roof claim denial florida, denied roof claim florida, roof insurance claim denied florida, hurricane roof claim denied florida, hail roof claim denied florida, roof replacement claim denied florida, florida roof claim public adjuster, denied roof claim help florida, reopen roof claim florida, roof age denial florida",

    image: images.roof,
    showStats: true,

    painSectionTitle: "Why Florida Roof Claims Get Denied",
    painSectionSub:
      "Florida's roof claim landscape has changed dramatically in the last 5 years. These are the denial reasons we defeat constantly:",
    painPoints: [
      {
        title: "Roof age cited as denial reason",
        text: "Carriers in Florida frequently deny otherwise valid wind and hail damage claims because the roof is 10, 15, or 20+ years old. Roof age alone is not a valid denial — storm-caused damage is covered regardless of roof age, provided it's documented properly.",
      },
      {
        title: "'No functional damage' / cosmetic only",
        text: "The most common denial language for hail and wind claims. Carriers send a quick adjuster who claims the damage is cosmetic only. A proper HAAG-certified inspection with chalk-tested test squares often proves functional damage that requires replacement.",
      },
      {
        title: "Hidden underlayment damage missed",
        text: "Carrier adjusters often don't pull shingles to inspect underlayment, flashing, or decking. Hidden damage is missed — and the partial-repair offer doesn't restore the roof system.",
      },
      {
        title: "Wear and tear / pre-existing condition",
        text: "Carriers love this one for roofs. They claim the damage was pre-existing or due to maintenance. Meteorological data tying damage to a specific storm event defeats this.",
      },
      {
        title: "Matching / repair vs replace dispute",
        text: "Florida's matching statute (Fla. Stat. § 626.9744) entitles homeowners to a uniform appearance when repairs can't match. Carriers routinely violate this. We enforce it.",
      },
      {
        title: "Citizens / FL Peninsula / state-affiliated denials",
        text: "Florida's state-supported and surplus carriers issue denials at high rates. They can still be reversed — they just take more documentation pressure.",
      },
    ],

    stepsSectionTitle: "How We Reopen a Denied Roof Claim in Florida",
    steps: [
      {
        n: "01",
        title: "Pull the denial letter and your policy",
        text: "We identify the specific denial reason, the policy provisions involved, and the exact legal and factual basis to dispute.",
      },
      {
        n: "02",
        title: "Order a proper roof inspection",
        text: "HAAG-certified inspector or licensed roofing contractor walks the roof, performs chalk-tested test squares for hail, documents wind uplift, and inspects underlayment.",
      },
      {
        n: "03",
        title: "Pull the meteorological data",
        text: "We obtain NOAA storm-event verification tying your damage to a specific date and storm — defeating wear-and-tear arguments.",
      },
      {
        n: "04",
        title: "Build the replacement Xactimate scope",
        text: "Full roof replacement scope including code upgrades (decking, secondary water barrier, hurricane straps, etc.), labor, materials, and O&P at Florida market rates.",
      },
      {
        n: "05",
        title: "Formal demand and negotiation",
        text: "We file the formal supplemental demand with complete documentation. Most denied Florida roof claims are reversed at this stage. If not, we have escalation options.",
      },
    ],

    statsBlock: {
      headline: "Florida roof claims we've reopened.",
      stats: [
        { value: "Hundreds", label: "Florida roof claims reopened" },
        { value: "100%", label: "Roof replacement on qualifying losses" },
        { value: "$0", label: "Upfront — we only get paid if you do" },
      ],
    },

    faqs: [
      {
        question: "My Florida roof claim was denied for roof age — is that legal?",
        answer:
          "Roof age alone is not a valid denial reason under Florida law. While carriers may apply specific roof-age endorsements (such as ACV-only schedules for older roofs), they cannot deny a covered storm loss simply because the roof is old. Wind, hail, and hurricane damage are covered perils regardless of roof age, provided the damage is properly documented.",
      },
      {
        question: "What is Florida's matching statute and how does it help my roof claim?",
        answer:
          "Fla. Stat. § 626.9744 — Florida's matching statute — requires insurers to repair damaged property with materials of like kind and quality. If a damaged section of roof cannot be matched (discontinued shingles, color variance), the carrier must pay for replacement of the larger area to restore uniform appearance. This is a powerful tool in roof claim disputes.",
      },
      {
        question: "How long do I have to dispute a denied roof claim in Florida?",
        answer:
          "Under Fla. Stat. § 627.70132, you generally have 1 year from the date of loss to file a hurricane-related claim and 18 months to file a supplemental or reopened hurricane claim. For non-hurricane perils, deadlines vary — we review your specific policy at no cost.",
      },
      {
        question: "What's the difference between cosmetic and functional roof damage in Florida?",
        answer:
          "'Cosmetic' damage doesn't impair the roof's ability to shed water; 'functional' damage does. Carriers exploit this distinction to deny hail claims. A proper HAAG-certified inspection with chalk-tested test squares can prove functional damage that requires roof replacement under the policy.",
      },
      {
        question: "Will my premium go up if I reopen a denied roof claim?",
        answer:
          "Reopening a previously filed claim with the same insurer for the same loss generally does not count as a 'new claim' for rating purposes. Florida law also restricts non-renewal based on legitimate claim activity. We handle the supplemental process properly to protect your standing.",
      },
      {
        question: "Do I need a roof inspection before calling a public adjuster?",
        answer:
          "No. We coordinate the entire inspection process — usually with a HAAG-certified inspector or licensed Florida roofing contractor — as part of the free claim review. You don't pay for any inspections we order.",
      },
    ],

    relatedTitle: "Related Florida Claim Help",
    related: [
      {
        text: "Insurance claim denied in Florida?",
        href: "/denied-insurance-claim-florida",
      },
      {
        text: "Underpaid insurance claim in Florida?",
        href: "/underpaid-insurance-claim-florida",
      },
      { text: "Hail roof damage claim help", href: "/hail-damage" },
      { text: "Wind / hurricane damage claim help", href: "/wind-damage" },
      { text: "Read our blog: Top 5 reasons Florida claims get denied", href: "/blog/top-5-reasons-florida-claims-get-denied" },
    ],
    extraSections: [
      {
        h2: "Hurricane Isaias Roof Claim Denials in Northwest Florida",
        body: [
          [
            "Hurricane Isaias crossed the Panhandle coast near Destin on October 9, 2026 with 105 mph sustained winds, and preliminary NWS reports recorded gusts of 117 mph at Oriole Beach and 116 mph near Santa Rosa Sound. Roof denials in the storm's footprint lean on salt-air wear, roof age, and \"repairable\" findings on metal, tile, and membrane systems. See how we document Panhandle roof losses on our ",
            { to: "/pensacola-roof-damage-claims", label: "Pensacola roof damage claims" },
            " and ",
            { to: "/destin-roof-damage-claims", label: "Destin roof damage claims" },
            " pages.",
          ],
        ],
      },
    ],

    guides: [
      {
        tag: "Roof Claims Guide",
        title: "Why Insurance Companies Deny Roof Claims in Florida",
        href: "/blog/why-insurance-companies-deny-roof-claims-in-florida",
      },
      {
        tag: "Roof Claims Guide",
        title: "What To Do If Your Florida Roof Claim Is Denied",
        href: "/blog/what-to-do-if-your-florida-roof-claim-is-denied",
      },
      {
        tag: "Estimate First",
        title: "Roof Claim Calculator — See What Your Roof Claim Is Worth",
        href: "/roof-claim-calculator",
      },
    ],
  },
};

// Helper for linking from anywhere
export const landingPageUrl = (slug) => `/${slug}`;
